import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { getActivityById, updateActivity } from '~/services/activityService';
import CustomEditor from '~/components/CustomEditor';
import PushNotification from '~/components/PushNotification';
import styles from './UpdateRecruitment.module.scss';
import { useParams, useNavigate } from 'react-router-dom';
import LoadingScreen from '~/components/LoadingScreen';
import routes from '~/config/routes';
import Title from '~/components/Title';

const UpdateRecruitment = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [notification, setNotification] = useState({ message: '', type: '' });
    const [initialValues, setInitialValues] = useState(null);

    const validationSchema = Yup.object({
        title: Yup.string().required('Tiêu đề là bắt buộc'),
        summary: Yup.string().required('Tóm tắt là bắt buộc'),
        image: Yup.mixed().required('Hình ảnh là bắt buộc'),
        content: Yup.string().required('Nội dung là bắt buộc'),
        isFeatured: Yup.boolean(),
    });

    useEffect(() => {
        const fetchRecruitment = async () => {
            try {
                const recruitment = await getActivityById(id);
                setInitialValues({
                    title: recruitment.title,
                    summary: recruitment.summary,
                    image: recruitment.images,
                    categoryId: recruitment.categoryId,
                    content: recruitment.content,
                    isFeatured: recruitment.isFeatured,
                });
            } catch (error) {
                console.error('Lỗi khi tải hoạt động:', error);
            }
        };

        fetchRecruitment();
    }, [id]);

    const handleImageUpload = (event, setFieldValue) => {
        const file = event.target.files[0];
        setFieldValue('image', file);
    };

    const handleSubmit = async (values, { resetForm }) => {
        const formData = new FormData();

        formData.append('title', values.title);
        formData.append('summary', values.summary);

        if (values.image) {
            formData.append('image', values.image);
        } else {
            formData.append('image', initialValues.image);
        }

        formData.append('categoryId', values.categoryId);
        formData.append('content', values.content);
        formData.append('isFeatured', values.isFeatured);

        try {
            await updateActivity(id, formData);
            setNotification({ message: 'Cập nhật hoạt động thành công!', type: 'success' });
            resetForm();
            setTimeout(() => {
                navigate(routes.recruitmentList);
            }, 1000);
        } catch (error) {
            setNotification({ message: 'Lỗi khi cập nhật hoạt động.', type: 'error' });
            console.error('Lỗi khi cập nhật hoạt động:', error);
        }
    };

    if (!initialValues) {
        return <LoadingScreen />;
    }

    return (
        <div className={styles.editRecruitment}>
            <Title text="Cập nhật hoạt động" />
            {notification.message && <PushNotification message={notification.message} type={notification.type} />}
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                {({ isSubmitting, setFieldValue, values }) => (
                    <Form className={styles.form}>
                        <div className={styles.formGroup}>
                            <label htmlFor="title">Tiêu Đề</label>
                            <Field name="title" type="text" className={styles.input} />
                            <ErrorMessage name="title" component="div" className={styles.error} />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="summary">Tóm Tắt</label>
                            <Field name="summary" type="text" className={styles.input} />
                            <ErrorMessage name="summary" component="div" className={styles.error} />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Chọn Hình Ảnh</label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(event) => handleImageUpload(event, setFieldValue)}
                            />
                            <ErrorMessage name="image" component="div" className={styles.error} />
                        </div>
                        {values.image && (
                            <div className={styles.imagePreview}>
                                <img
                                    src={
                                        typeof values.image === 'string'
                                            ? `${process.env.REACT_APP_HOST}/${values.image}`
                                            : URL.createObjectURL(values.image)
                                    }
                                    alt="Preview"
                                    className={styles.recruitmentImage}
                                />
                            </div>
                        )}
                        <div className={styles.formGroup}>
                            <label htmlFor="content">Nội Dung</label>
                            <CustomEditor
                                onChange={(content) => setFieldValue('content', content)}
                                initialValue={values.content}
                            />
                            <ErrorMessage name="content" component="div" className={styles.error} />
                        </div>
                        <div className={styles.formGroup}>
                            <label>
                                <Field type="checkbox" name="isFeatured" />
                                Đánh dấu là nổi bật
                            </label>
                        </div>
                        <button type="submit" disabled={isSubmitting} className={styles.submitButton}>
                            Cập Nhật Hoạt động
                        </button>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default UpdateRecruitment;
